import { z } from 'zod';
import { FilterGroupSchema, GroupLogicSchema } from './filter';
import { FlowListLayoutIdSchema } from './flow-layouts';

/**
 * FlowView — one shape that both account and contact tables instantiate.
 * It is also the shared query-builder input, so views stay MCP-queryable.
 *
 * v0: presets are defined in code with `createdBy: null` — there is no
 * persistence. Per-rep saved views (`flow_views`) are v1.
 *
 * `.strict()`: rejects unknown keys, including the retired `ownerUserId`
 * field name and the retired `scope` field — a payload still carrying
 * either fails to parse instead of silently stripping the key.
 *
 * NO `scope` (U5 / R2 — "no hidden filter state"). `scope: 'my'|'team'|'all'`
 * used to narrow a list by ownership without appearing anywhere the rep could
 * see or clear it. Ownership is now an ordinary VISIBLE filter row on a
 * person-typed field (`claydr_name`/`owner_name`/`gs_name`, or on contacts
 * `account_claydr_name`/`account_owner_name`) carrying the `{me}` token,
 * resolved per viewer server-side (see src/services/flow/me-token.ts). The one
 * surviving non-filter narrowing is the accounts bulk-provenance guard, which
 * is a universe definition rather than view state and therefore lives in the
 * accounts handler's base WHERE, not on this schema (Decision 8).
 */

export const FlowSortSchema = z.object({
  field: z.string(),
  dir: z.enum(['ASC', 'DESC']),
});
export type FlowSort = z.infer<typeof FlowSortSchema>;

/** A view's `groupBy` may carry up to this many ordered levels (R5). */
export const MAX_GROUP_LEVELS = 3;

/**
 * Canonical `groupBy` normalizer — the ONE place every legacy shape collapses
 * into the ordered, deduped array a view's grouping actually is (R5/R3):
 *
 *  - `undefined` / `null` / `''` → `[]` (no grouping).
 *  - a bare string → split on `,` — this is what makes a comma-joined
 *    `flow_views.group_by` DB value (`'tracker_rank,tracker_date'`) round-trip
 *    back into its levels on read, and also degrades a legacy single-field
 *    string (`'tracker'`, no comma) into the one-item list it always meant.
 *  - an array → deduped, keeping the FIRST occurrence of each key (order is
 *    the rep's intent, and a duplicate level contributes nothing beyond its
 *    first appearance).
 *  - an EMPTY or whitespace-only segment is dropped, not kept (R29): a
 *    pre-deploy tab sends `group_by=` for an ungrouped view, and `''.split(',')`
 *    is `['']`, which the list routes 400 as `Unknown group_by field: `. An
 *    empty segment is the ABSENCE of a level, never a level named "".
 *
 * Deliberately permissive about non-string members inside an array input —
 * this is a `z.preprocess` step, not the validator: `z.array(z.string())`
 * downstream is what actually rejects a non-string level.
 */
export function normalizeGroupBy(input: unknown): string[] {
  if (input === undefined || input === null || input === '') return [];
  const raw: unknown[] = Array.isArray(input)
    ? input
    : typeof input === 'string'
      ? input.split(',')
      : [input];
  const seen = new Set<unknown>();
  const out: unknown[] = [];
  for (const v of raw) {
    // Blank segments are dropped BEFORE the dedupe so `'a,,b'` and `'a, ,b'`
    // both yield ['a','b'] rather than a level named '' (R29).
    if (typeof v === 'string' && v.trim() === '') continue;
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out as string[];
}

/** Inverse of `normalizeGroupBy` for the `flow_views.group_by` `String?`
 *  column (D5 — no migration; comma is the delimiter, enforced comma-free by
 *  the catalog-key test in `__tests__/view-groupby.test.ts`). */
export function serializeGroupBy(levels: string[]): string | null {
  return levels.length === 0 ? null : levels.join(',');
}

const GroupBySchema = z.preprocess(normalizeGroupBy, z.array(z.string()).max(MAX_GROUP_LEVELS));

export const FlowViewSchema = z.object({
  id: z.string(),
  /** null = system preset; set = a rep's custom view. DB ids are stringified. */
  createdBy: z.string().nullable(),
  entity: z.enum(['contact', 'account', 'signal', 'play']),
  name: z.string(),
  filter: z.array(FilterGroupSchema),
  /** Outer AND/OR combinator across filter groups. Absent on code-defined
   *  presets (they have no stored combinator to carry); saved views always
   *  send an explicit value now that `flow_views.group_logic` exists. */
  groupLogic: GroupLogicSchema.optional(),
  sort: FlowSortSchema,
  /** Ordered, up-to-`MAX_GROUP_LEVELS` field-key list (R5). Always present as
   *  an array — `[]` means "no grouping", the same array-shaped contract
   *  `filter`/`columns` already carry. See `normalizeGroupBy`. */
  groupBy: GroupBySchema,
  /** Ordered field-key list — visible columns, in order. */
  columns: z.array(z.string()),
  isDefault: z.boolean(),
  /** Code presets and rep-saved views may pin the list layout the view opens with.
   *  Not part of ViewDefinitionShape, so a pin never dirties a view. */
  flowLayout: FlowListLayoutIdSchema.optional(),
}).strict();
export type FlowView = z.infer<typeof FlowViewSchema>;

/**
 * Saved-view wire shape — `FlowViewSchema` plus the fields the views list
 * endpoint joins in (creator display name, timestamps, usage count). Lives
 * here (not a second file) per `terra/no-duplicate-schema-name` — one
 * schema name, one source location.
 */
export const SavedViewWireSchema = FlowViewSchema.extend({
  /** Resolved display name of `createdBy`; null for presets (no creator). */
  creatorName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  usageCount: z.number(),
});
export type SavedViewWire = z.infer<typeof SavedViewWireSchema>;

/** `GET /api/flow/views?entity=` — every saved view for that entity. The
 *  two-tier split (Your views / Team views) is a client-side partition on
 *  `createdBy`, not a second endpoint: a rep can open anyone's view.
 *
 * `presetUsage` (F12) — `flow_view_usage` is keyed by the view-id STRING
 * specifically so a code-defined preset slug, which has no `flow_views` row
 * to join a `usageCount` onto inline on a `SavedViewWire`, is still counted
 * (Decision 4). This is the read-back path for that counter: a map from
 * preset id (scoped to THIS entity's presets) to its open count. A preset
 * that has never been opened — or was opened before this field existed — is
 * simply absent from the map rather than present with a fabricated `0`. */
export const FlowViewsResponseSchema = z.object({
  views: z.array(SavedViewWireSchema),
  presetUsage: z.record(z.string(), z.number()),
});
export type FlowViewsResponse = z.infer<typeof FlowViewsResponseSchema>;

/**
 * The dirty-relevant definition a saved view stores. Mirrors
 * `FlowWorkingStateSchema` — `flow_views.group_logic` persists the outer
 * AND/OR combinator across filter groups (D2); `groupLogic` is optional here
 * so a partial PUT (e.g. a rename, or an older client) can omit it and fall
 * back to the stored value.
 */
const ViewDefinitionShape = {
  filter: z.array(FilterGroupSchema),
  groupLogic: GroupLogicSchema.optional(),
  sort: FlowSortSchema,
  /** Required/array-shaped here — `SavedViewCreateBodySchema` below uses this
   *  directly (an omitted `groupBy` on create normalizes to `[]`, same as any
   *  other `GroupBySchema` consumer). `SavedViewUpdateBodySchema` wraps this
   *  in its OWN `.optional()` so a partial PUT can still distinguish
   *  "untouched" (key absent → `undefined`, short-circuited by that outer
   *  `ZodOptional` before `normalizeGroupBy` ever runs) from "clear grouping"
   *  (`null` or `[]`, which still reaches `normalizeGroupBy` → `[]`). */
  groupBy: GroupBySchema,
  columns: z.array(z.string()),
};

const ViewNameSchema = z.string().trim().min(1).max(200);

/** `POST /api/flow/views` — `createdBy` is the acting user, never client-supplied. */
export const SavedViewCreateBodySchema = z.object({
  entity: z.enum(['contact', 'account', 'signal', 'play']),
  name: ViewNameSchema,
  ...ViewDefinitionShape,
  flowLayout: FlowListLayoutIdSchema.optional(),
}).strict();
export type SavedViewCreateBody = z.infer<typeof SavedViewCreateBodySchema>;

/**
 * `PUT /api/flow/views/:id` — every field optional so rename can send only
 * `{ name }` (rename rides this route and is NOT part of dirty state) while
 * Save sends the whole definition. `entity` is absent by design: a view
 * cannot change entity — its columns and filter fields belong to one
 * catalog.
 */
export const SavedViewUpdateBodySchema = z.object({
  name: ViewNameSchema.optional(),
  filter: ViewDefinitionShape.filter.optional(),
  groupLogic: ViewDefinitionShape.groupLogic,
  sort: ViewDefinitionShape.sort.optional(),
  groupBy: ViewDefinitionShape.groupBy.optional(),
  columns: ViewDefinitionShape.columns.optional(),
  flowLayout: FlowListLayoutIdSchema.optional(),
}).strict().refine(
  body => Object.keys(body).length > 0,
  { message: 'At least one field must be provided' },
);
export type SavedViewUpdateBody = z.infer<typeof SavedViewUpdateBodySchema>;
